<template>
  <section class="invoices-toolbar app-filter-toolbar">
    <div
      class="toolbar-main-row app-filter-toolbar-row invoices-toolbar-row"
      :class="{ 'invoices-toolbar-row--open': showFilters }"
    >
      <div class="search-wrap app-filter-search-wrap">
        <i class="fa fa-search" aria-hidden="true"></i>
        <input
          v-model="search"
          type="text"
          :placeholder="labels.searchPlaceholder"
          @input="emitSearch"
          @keyup.enter="emitApply"
        />
      </div>

      <div class="toolbar-right app-filter-toolbar-right">
        <AppFilterDateRange
          v-model="dateRangeValue"
          class="app-filter-date-inline"
          label=""
          :show-input="showDateInput"
          :presets="labels.dateRangePresets"
          initial-preset-key="this_month"
          @change="onDateRangeChange"
        />

        <multiselect
          v-model="filters.customer"
          :options="options.customer"
          track-by="value"
          label="label"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          :show-labels="false"
          :searchable="filterConfigByKey('customer')?.searchable"
          :allow-empty="true"
          :max-height="220"
          :placeholder="filterConfigByKey('customer')?.placeholder"
          class="invoices-toolbar-customer-inline customer-filter-select app-filter-select app-select app-select--filters"
          @select="onSelect('customer', $event)"
          @remove="onRemove('customer')"
          @close="flushFiltersEmit"
        >
          <template #selection="{ values, isOpen }">
            <span v-if="!isOpen" class="multiselect__single">
              {{ selectionLabel('customer', values) }}
            </span>
          </template>
          <template #noResult>
            <span>{{ labels.emptyOption }}</span>
          </template>
          <template #noOptions>
            <span>{{ labels.emptyOption }}</span>
          </template>
        </multiselect>

        <button
          type="button"
          class="icon-btn app-filter-icon-btn"
          :class="{ active: showFilters }"
          :title="labels.filtersButtonTitle"
          @click="toggleFilters"
        >
          <i class="fa fa-filter" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="icon-btn app-filter-icon-btn sales-toolbar-search-btn"
          :title="labels.searchButtonTitle"
          @click="emitApply"
        >
          <i class="fa fa-search" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <transition name="filters-expand" @after-leave="handleFiltersAfterLeave">
      <div v-show="showFilters" class="filters-panel app-filter-panel">
        <div class="invoices-filters-grid invoices-filters-grid--first-row">
          <div
            v-for="filter in firstRowFilterConfigs"
            :key="filter.key"
            class="filter-field app-filter-field"
          >
            <span>{{ filter.label }}</span>
            <multiselect
              v-model="filters[filter.key]"
              :options="options[filter.key]"
              track-by="value"
              label="label"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :show-labels="false"
              :searchable="filter.searchable"
              :allow-empty="true"
              :max-height="220"
              :placeholder="filter.placeholder"
              class="customer-filter-select app-filter-select app-select app-select--filters"
              @select="onSelect(filter.key, $event)"
              @remove="onRemove(filter.key)"
              @close="flushFiltersEmit"
            >
              <template #selection="{ values, isOpen }">
                <span v-if="!isOpen" class="multiselect__single">
                  {{ selectionLabel(filter.key, values) }}
                </span>
              </template>
              <template #noResult>
                <span>{{ labels.emptyOption }}</span>
              </template>
              <template #noOptions>
                <span>{{ labels.emptyOption }}</span>
              </template>
            </multiselect>
          </div>
        </div>

        <div v-if="secondRowFilterConfigs.length > 0" class="invoices-filters-grid invoices-filters-grid--second-row">
          <div
            v-for="filter in secondRowFilterConfigs"
            :key="filter.key"
            class="filter-field app-filter-field"
          >
            <span>{{ filter.label }}</span>
            <multiselect
              v-model="filters[filter.key]"
              :options="options[filter.key]"
              track-by="value"
              label="label"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :show-labels="false"
              :searchable="filter.searchable"
              :allow-empty="true"
              :max-height="220"
              :placeholder="filter.placeholder"
              class="customer-filter-select app-filter-select app-select app-select--filters"
              @select="onSelect(filter.key, $event)"
              @remove="onRemove(filter.key)"
              @close="flushFiltersEmit"
            >
              <template #selection="{ values, isOpen }">
                <span v-if="!isOpen" class="multiselect__single">
                  {{ selectionLabel(filter.key, values) }}
                </span>
              </template>
              <template #noResult>
                <span>{{ labels.emptyOption }}</span>
              </template>
              <template #noOptions>
                <span>{{ labels.emptyOption }}</span>
              </template>
            </multiselect>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import AppFilterDateRange from '@/components/common/AppFilterDateRange.vue';
import {
  ALL_OPTION_VALUE,
  applyRemoveOption,
  applySelectOption,
  buildSelectedValues,
  ensureDefaultSelection,
} from '@/utils/filterSelectors';
import {
  buildToolbarApplyPayload,
  buildToolbarSelectionLabel,
  createToolbarAllOption,
  extractToolbarDateRange,
  mapToolbarOptions,
  normalizeToolbarDate,
  resolveToolbarToggleState,
  shouldEmitToolbarDateChange,
} from '@/composables/shared/useFilterToolbarShared';

const FILTER_KEYS = ['customer', 'hasBusiness', 'business', 'hasReceivedProof', 'invoiceType'];

export default {
  name: 'InvoicesToolbar',
  components: {
    AppFilterDateRange,
  },
  props: {
    filterOptions: {
      type: Object,
      default: () => ({}),
    },
    labels: {
      type: Object,
      required: true,
    },
  },
  emits: ['search-change', 'filters-change', 'apply'],
  data() {
    const options = {};
    const filters = {};

    FILTER_KEYS.forEach((key) => {
      const allOption = this.createAllOptionForKey(key);
      options[key] = [allOption];
      filters[key] = [allOption];
    });

    return {
      search: '',
      showFilters: false,
      showDateInput: false,
      dateRangeValue: null,
      filtersEmitDebounceMs: 1000,
      filtersEmitTimer: null,
      options,
      filters,
      filterConfigs: [
        {
          key: 'customer',
          label: String(this.labels?.filters?.customer?.label || ''),
          placeholder: String(this.labels?.filters?.customer?.placeholder || ''),
          searchable: Boolean(this.labels?.filters?.customer?.searchable),
        },
        {
          key: 'hasBusiness',
          label: String(this.labels?.filters?.hasBusiness?.label || ''),
          placeholder: String(this.labels?.filters?.hasBusiness?.placeholder || ''),
          searchable: Boolean(this.labels?.filters?.hasBusiness?.searchable),
        },
        {
          key: 'business',
          label: String(this.labels?.filters?.business?.label || ''),
          placeholder: String(this.labels?.filters?.business?.placeholder || ''),
          searchable: Boolean(this.labels?.filters?.business?.searchable),
        },
        {
          key: 'hasReceivedProof',
          label: String(this.labels?.filters?.hasReceivedProof?.label || ''),
          placeholder: String(this.labels?.filters?.hasReceivedProof?.placeholder || ''),
          searchable: Boolean(this.labels?.filters?.hasReceivedProof?.searchable),
        },
        {
          key: 'invoiceType',
          label: String(this.labels?.filters?.invoiceType?.label || ''),
          placeholder: String(this.labels?.filters?.invoiceType?.placeholder || ''),
          searchable: Boolean(this.labels?.filters?.invoiceType?.searchable),
        },
      ],
    };
  },
  computed: {
    firstRowFilterConfigs() {
      // `invoiceType` se habilitara en una siguiente iteracion.
      return ['hasBusiness', 'business', 'hasReceivedProof']
        .map((key) => this.filterConfigByKey(key))
        .filter(Boolean);
    },
    secondRowFilterConfigs() {
      return [];
    },
  },
  watch: {
    filterOptions: {
      deep: true,
      immediate: true,
      handler() {
        this.syncOptionsFromProps();
      },
    },
  },
  beforeUnmount() {
    this.clearFiltersEmitTimer();
  },
  methods: {
    createAllOptionForKey(key) {
      return createToolbarAllOption(this.labels?.allOptionLabels, key);
    },
    filterConfigByKey(key) {
      return this.filterConfigs.find((item) => item.key === key) || null;
    },
    toggleFilters() {
      const nextState = resolveToolbarToggleState(this.showFilters);
      this.showFilters = nextState.showFilters;

      if (nextState.showDateInput !== null) {
        this.showDateInput = nextState.showDateInput;
      }
    },
    handleFiltersAfterLeave() {
      this.showDateInput = false;
    },
    clearFiltersEmitTimer() {
      if (!this.filtersEmitTimer) {
        return;
      }

      clearTimeout(this.filtersEmitTimer);
      this.filtersEmitTimer = null;
    },
    scheduleFiltersEmit() {
      this.clearFiltersEmitTimer();

      this.filtersEmitTimer = setTimeout(() => {
        this.filtersEmitTimer = null;
        this.emitFilters();
      }, this.filtersEmitDebounceMs);
    },
    flushFiltersEmit() {
      this.clearFiltersEmitTimer();
      this.emitFilters();
    },
    normalizeDate(value) {
      return normalizeToolbarDate(value);
    },
    onDateRangeChange(payload) {
      if (shouldEmitToolbarDateChange(payload, this.dateRangeValue)) {
        this.flushFiltersEmit();
      }
    },
    extractDateRange() {
      return extractToolbarDateRange(this.dateRangeValue, {
        startKey: 'date_from',
        endKey: 'date_to',
      });
    },
    emitSearch() {
      this.$emit('search-change', this.search);
    },
    emitFilters() {
      const payload = {
        ...this.extractDateRange(),
        customer_ids: buildSelectedValues(this.filters.customer),
        has_business: buildSelectedValues(this.filters.hasBusiness),
        business_ids: buildSelectedValues(this.filters.business),
        has_received_proof: buildSelectedValues(this.filters.hasReceivedProof),
        invoice_types: buildSelectedValues(this.filters.invoiceType),
      };

      this.$emit('filters-change', payload);
      return payload;
    },
    emitApply() {
      this.emitSearch();
      const filters = this.emitFilters();
      this.$emit('apply', buildToolbarApplyPayload(this.search, filters));
    },
    onSelect(filterKey, option) {
      this.filters[filterKey] = applySelectOption(
        this.filters[filterKey],
        option,
        this.createAllOptionForKey(filterKey)
      );

      this.scheduleFiltersEmit();
    },
    onRemove(filterKey) {
      this.filters[filterKey] = applyRemoveOption(
        this.filters[filterKey],
        this.createAllOptionForKey(filterKey)
      );

      this.scheduleFiltersEmit();
    },
    selectionLabel(filterKey, values) {
      const selected = Array.isArray(values) ? values : [];
      const hasAllOption = selected.some((item) => item?.value === ALL_OPTION_VALUE);
      const customAllLabel = String(this.labels?.selectionAllLabels?.[filterKey] || '').trim();

      if (hasAllOption && customAllLabel !== '') {
        return buildToolbarSelectionLabel(values, customAllLabel);
      }

      if (selected.length === 0 && customAllLabel !== '') {
        return buildToolbarSelectionLabel(values, customAllLabel);
      }

      return buildToolbarSelectionLabel(values);
    },
    syncOptionsFromProps() {
      const nextOptions = {};
      const nextFilters = {};

      FILTER_KEYS.forEach((key) => {
        const allOption = this.createAllOptionForKey(key);
        const list = mapToolbarOptions(this.filterOptions?.[key] || []);
        const allowedValues = new Set([allOption.value, ...list.map((item) => item?.value)]);
        const currentSelection = Array.isArray(this.filters?.[key]) ? this.filters[key] : [];
        const validSelection = currentSelection.filter((item) => allowedValues.has(item?.value));

        nextOptions[key] = [allOption, ...list];
        nextFilters[key] = ensureDefaultSelection(validSelection, allOption);
      });

      this.options = {
        ...this.options,
        ...nextOptions,
      };

      this.filters = {
        ...this.filters,
        ...nextFilters,
      };
    },
  },
};
</script>

<style scoped>
.invoices-toolbar-row--open {
  align-items: flex-start;
}

.invoices-toolbar-row--open .toolbar-right {
  align-items: flex-start;
}

.invoices-toolbar-row--open .toolbar-right > * {
  align-self: flex-start;
}

.invoices-toolbar-row--open .invoices-toolbar-customer-inline {
  margin-top: 0;
}

.invoices-filters-grid {
  display: grid;
  gap: 10px;
}

.invoices-filters-grid--first-row {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.invoices-filters-grid--second-row {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 10px;
}

.invoices-toolbar-customer-inline {
  width: 238px;
  min-width: 238px;
  flex: 0 0 238px;
  margin-top: 4px;
}

.invoices-toolbar-customer-inline :deep(.multiselect__tags) {
  min-height: 36px;
  height: auto;
}

.sales-toolbar-search-btn {
  background-color: #0070cd;
  border-color: #0070cd;
  color: #ffffff;
}

.sales-toolbar-search-btn i {
  color: #ffffff;
}

.sales-toolbar-search-btn:hover,
.sales-toolbar-search-btn:focus {
  background-color: #005eae;
  border-color: #005eae;
}

@media (max-width: 1200px) {
  .invoices-filters-grid--first-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .invoices-filters-grid--second-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .invoices-filters-grid--first-row,
  .invoices-filters-grid--second-row {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
